import { useApolloClient } from "@apollo/client";
import { CircularProgress, Typography, IconButton } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import { makeStyles } from "@material-ui/core/styles";
import CloudUpload from "@material-ui/icons/CloudUpload";
import DeleteIcon from "@material-ui/icons/Delete";
import React, { useRef, useState } from "react";
import Files from "react-files";
import { FormattedMessage, IntlShape, injectIntl } from "react-intl";
import {
  FileDownloadInterface,
  FileInterfaceLinkcy,
} from "../../../interface/FileInterface";
import { removeSpecialChars } from "../../../utils/common";
import { MESSAGE_TYPE } from "../../../utils/constant";
import { displaySnackBar } from "../../../utils/snackBarUtils";
import FileUploadFragment from "../FileUploadFragment/FileUploadFragment";

const useStyles = makeStyles((theme) => ({
  files: {
    width: "100%",
    minWidth: 150,
    maxWidth: 450,
    height: 56,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 2),
    fontSize: ".85rem",
    color: "gray",
    backgroundColor: theme.palette.common.white,
    borderRadius: 5,
    border: `1px dashed #CCC`,
  },
  listFile: {
    minWidth: 50,
    maxHeight: 50,
    flexGrow: 0,
    height: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginRight: "20px",
    marginBottom: theme.spacing(1),
  },
  deleteIcon: {
    marginLeft: "auto",
  },
  smallFont: {
    fontSize: 10,
  },
  errorText: {
    fontSize: 12,
    color: "red",
    paddingLeft: 15,
  },
}));

interface FileUploadPropsInterface {
  intl: IntlShape;
  label: string;
  isLoading?: boolean;
  fileData?: FileInterfaceLinkcy;
  fileMultiple?: FileInterfaceLinkcy[];
  isSubmitted?: boolean;
  multiple?: boolean;
  category: string;
  setDocument(Files: FileInterfaceLinkcy[], isVerso?: boolean): void;
  onRemove?: (type: string) => void;
  disabled?: boolean;
}

const FileUpload = (props: FileUploadPropsInterface) => {
  const {
    setDocument,
    label,
    fileData: initialFileData,
    fileMultiple,
    intl,
    isLoading,
    multiple,
    category,
    onRemove,
    disabled,
  } = props;
  const [fileData, setFileData] = useState(initialFileData);
  const [fileMultiples, setFileMultiples] = useState(fileMultiple);
  const isSubmitted = props.isSubmitted ? props.isSubmitted : false;
  const classes = useStyles();
  const filesRef = useRef<any>();
  const initialFileList: File[] = [];
  const [files, setFiles] = useState(initialFileList);
  const client = useApolloClient();
  const result: any = [];

  const onFilesChange = (files: File[]) => {
    if (disabled) return;
    files.forEach((file) => {
      result.push({
        filename: removeSpecialChars(file.name),
        filetype: file.type,
        file,
        category,
        label,
      });
    });
    setFiles(files);
    setDocument(result);
  };

  const onFilesError = (error: any) => {
    const { code } = error;
    let message: string = "";
    switch (code) {
      case 1:
        message = intl.formatMessage({ id: "error.uploadFileInvalid" });
        break;
      case 2:
        message = intl.formatMessage({ id: "error.uploadFileTooLarge" });
        break;
      case 3:
        message = intl.formatMessage({ id: "error.uploadFileTooSmall" });
        break;
      case 4:
        message = intl.formatMessage({ id: "error.uploadOnlyOneFile" });
        break;
    }
    displaySnackBar(client, {
      type: MESSAGE_TYPE.ERROR,
      message,
      isOpen: true,
    });
  };

  const filesRemove = () => {
    setFiles([]);
    setFileMultiples(undefined);
    if (filesRef && filesRef.current) {
      filesRef.current.state.files = [];
    }
    setFileData(undefined);
    if (onRemove) {
      onRemove(category);
    }
  };

  return (
    <Box maxWidth={450}>
      <Box className="files">
        <Files
          ref={filesRef}
          onChange={onFilesChange}
          onError={onFilesError}
          accepts={["image/*", ".pdf"]}
          maxFiles={multiple ? 2 : 1}
          maxFileSize={10000000}
          minFileSize={0}
          clickable={true}
          className={classes.files}
          disabled={disabled}
        >
          <CloudUpload style={{ marginRight: "20px" }} />
          {label}
        </Files>
        {isSubmitted &&
          !files.length &&
          fileData &&
          fileData.fileUrl &&
          !fileData.fileUrl.length && (
            <Box className={classes.errorText}>
              <FormattedMessage id="error.fileNotSelected" />
            </Box>
          )}
      </Box>
      {isLoading && fileData && !!fileData.filename.length && (
        <CircularProgress />
      )}
      {fileData && !!fileData.base64Content && !files.length && (
        <Box className={classes.listFile}>
          <img
            alt={fileData?.filename ?? "document"}
            src={fileData?.base64Content ?? ""}
            className={classes.listFile}
          />
          <IconButton
            aria-label="delete"
            className={classes.deleteIcon}
            onClick={(e) => {
              e.preventDefault();
              setFileData(undefined);
              onRemove && onRemove(category);
            }}
            disabled={disabled}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )}

      {fileMultiples && fileMultiples?.length
        ? fileMultiples.map((_file) => {
            if (_file && !!_file.base64Content && !files.length)
              return (
                <Box className={classes.listFile}>
                  <img
                    alt={_file?.filename ?? "document"}
                    src={_file?.base64Content ?? ""}
                    className={classes.listFile}
                  />
                  <IconButton
                    aria-label="delete"
                    className={classes.deleteIcon}
                    onClick={(e) => {
                      e.preventDefault();
                      setFileMultiples(undefined);
                      onRemove && onRemove(category);
                    }}
                    disabled={disabled}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              );
          })
        : ""}

      {!!files.length && (
        <Box component={"ul"} display={"flex"} flexDirection={"column"}>
          {files.map((file: any, index: number) => (
            <FileUploadFragment
              file={file}
              isMultiple={!!multiple}
              key={`file-img-${index}`}
              handleRemove={filesRemove}
              disabled={disabled}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default injectIntl(FileUpload);
